// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The account chip in the app chrome. AYNI: driven by the site's own
 * accounts (`services/account.ts`, `docs/AYNI_XR_LICENSING.md`) rather than
 * terraviz's Cloudflare Access sign-in. Signed out, its menu offers Sign in
 * (Google or an emailed link; the first sign-in creates the account), Your
 * plan and Plans and pricing. Signed in, it shows the address, Your plan,
 * Plans and pricing, Sign out and Delete account; with two licences or
 * more (2026-10-10), the plan in use and **Switch**, which opens Your plan.
 */

import { NATIVE_NAMES, PICKER_LOCALES, getLocale, t, type Locale } from '../i18n'
import { saveLocalePref } from '../i18n/persistence'
import { accountState, loadAccount, onAccountChange, signOut, type AccountState } from '../services/account'
import { openDeleteAccount, openPlans, openSignIn, openYourPlan, planName } from './xrPlansUI'
import { openPasskeys } from './passkeysUI'
import { openMeetingPanel } from './meetingPanel'

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function initials(value: string): string {
  const clean = value.trim()
  if (!clean) return '?'
  const nameParts = clean.split(/\s+/).filter(Boolean)
  if (nameParts.length > 1) return (nameParts[0][0] + nameParts[1][0]).toUpperCase()
  const emailName = clean.split('@')[0] || clean
  return emailName.slice(0, 2).toUpperCase()
}

const planOptions = { onStartMeeting: (trigger: HTMLElement) => openMeetingPanel(trigger) }

let outsideClick: ((event: MouseEvent) => void) | null = null

/** The languages offered (each in its own name), the one in use selected. */
function languageOptions(): string {
  const active = getLocale()
  const shown: readonly Locale[] = PICKER_LOCALES.includes(active) ? PICKER_LOCALES : [...PICKER_LOCALES, active]
  return shown.map((l) => `<option value="${escapeHtml(l)}"${l === active ? ' selected' : ''}>${escapeHtml(NATIVE_NAMES[l] ?? l)}</option>`).join('')
}

function render(container: HTMLElement, state: AccountState): void {
  const signedIn = state.status === 'signedIn' && !!state.email
  const label = signedIn ? state.name || state.email! : t('account.menu.signIn')
  const avatar = signedIn ? initials(label) : 'A'
  const body = signedIn ? state.email! : t('account.menu.signedOutBody')
  // AYNI (several licences, 2026-10-10): with two or more, the plan in use and a way to switch.
  const inUsePlan = signedIn && state.licences.length > 1 && state.licence?.plan ? planName(state.licence.plan) : null
  const inUse = inUsePlan
    ? `<div class="account-in-use"><span>${escapeHtml(t('account.menu.inUse', { plan: inUsePlan }))}</span><button id="account-menu-switch" class="account-action" type="button">${escapeHtml(t('account.menu.switch'))}</button></div>`
    : ''
  const actions = signedIn
    ? `${state.ownData ? `<button id="account-menu-passkeys" class="account-action" type="button">${escapeHtml(t('account.menu.passkeys'))}</button>` : ''}
       <button id="account-menu-signout" class="account-action" type="button">${escapeHtml(t('account.menu.signOut'))}</button>
       <button id="account-menu-delete" class="account-action account-action-quiet" type="button">${escapeHtml(t('account.menu.delete'))}</button>`
    : `<button id="account-menu-signin" class="account-action account-action-primary" type="button">${escapeHtml(t('account.menu.signIn'))}</button>`

  container.innerHTML = `
    <button id="account-menu-button" class="account-button" type="button" aria-expanded="false" aria-haspopup="true">
      <span class="account-avatar" aria-hidden="true">${escapeHtml(avatar)}</span>
      <span class="account-label">${escapeHtml(label)}</span>
    </button>
    <div id="account-menu-popover" class="account-popover hidden" role="dialog" aria-label="${escapeHtml(t('account.menu.title'))}">
      <div class="account-popover-title">${escapeHtml(t(signedIn ? 'account.menu.signedIn' : 'account.menu.title'))}</div>
      <div class="account-popover-body">${escapeHtml(body)}</div>${inUse}
      <div class="account-popover-actions account-plans">
        <button id="account-menu-plan" class="account-action" type="button">${escapeHtml(t('account.menu.plan'))}</button>
        <button id="account-menu-plans" class="account-action" type="button">${escapeHtml(t('account.menu.plans'))}</button>
      </div>
      <div class="account-popover-actions">${actions}</div>
      <label class="account-language">
        <span>${escapeHtml(t('account.menu.language'))}</span>
        <select id="account-menu-language" class="account-language-select">${languageOptions()}</select>
      </label>
      <div class="account-popover-links">
        <a href="/about" target="_blank" rel="noopener">${escapeHtml(t('account.menu.about'))}</a>
        <a href="/manual" target="_blank" rel="noopener">${escapeHtml(t('account.menu.manual'))}</a>
        <a href="/privacy" target="_blank" rel="noopener">${escapeHtml(t('account.menu.privacy'))}</a>
        <a href="${escapeHtml(t('legal.termsUrl'))}" target="_blank" rel="noopener">${escapeHtml(t('account.menu.terms'))}</a>
      </div>
    </div>
  `

  const button = container.querySelector<HTMLButtonElement>('#account-menu-button')
  const popover = container.querySelector<HTMLElement>('#account-menu-popover')
  if (!button || !popover) return
  button.addEventListener('click', () => {
    const open = popover.classList.toggle('hidden') === false
    button.setAttribute('aria-expanded', open ? 'true' : 'false')
  })
  const closeMenu = (): void => {
    popover.classList.add('hidden')
    button.setAttribute('aria-expanded', 'false')
  }
  const on = (id: string, action: () => void): void => {
    container.querySelector(`#${id}`)?.addEventListener('click', () => {
      closeMenu()
      action()
    })
  }
  on('account-menu-plan', () => openYourPlan(button, planOptions))
  on('account-menu-switch', () => openYourPlan(button, planOptions))
  on('account-menu-plans', () => openPlans(button, undefined, planOptions))
  on('account-menu-signin', () => openSignIn(button))
  // AYNI: signed out, the address serves the welcome page again (functions/_gate.ts).
  on('account-menu-signout', () => void signOut().then(() => window.location.assign('/')))
  on('account-menu-delete', () => openDeleteAccount(button))
  // AYNI (decision O4): passkeys confirm risky actions on own data.
  on('account-menu-passkeys', () => openPasskeys(button))
  // AYNI (owner, 2026-10-11): the language, where everyone sees it (also in Tools → More). It reloads, as there.
  container.querySelector<HTMLSelectElement>('#account-menu-language')?.addEventListener('change', (event) => {
    saveLocalePref((event.target as HTMLSelectElement).value as Locale)
    window.location.reload()
  })
  if (outsideClick) document.removeEventListener('click', outsideClick)
  outsideClick = (event) => {
    if (container.contains(event.target as Node)) return
    closeMenu()
  }
  document.addEventListener('click', outsideClick)
}

export async function initAccountUI(): Promise<void> {
  const container = document.getElementById('account-control')
  if (!container) return
  container.classList.remove('hidden')
  onAccountChange((state) => render(container, state))
  if (accountState().status === 'unknown') await loadAccount()
}
